// Per-browser device identity for upload attribution. Browsers cannot expose
// MAC addresses, so a random id persisted in BOTH localStorage and a 1-year
// cookie (each restores the other) is the practical per-laptop fingerprint.

const ID_KEY = 'lms_device_id';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

const readCookie = (name: string): string | null => {
  const match = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

const writeCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${COOKIE_MAX_AGE}; path=/; SameSite=Lax; Secure`;
};

// Resolved once per session; the request interceptor calls these per request
let cachedId: string | null = null;
let cachedLabel: string | null = null;

export const getDeviceId = (): string => {
  if (cachedId) return cachedId;
  let id = localStorage.getItem(ID_KEY) || readCookie(ID_KEY);
  if (!id) {
    id =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  }
  // Re-persist in both stores so clearing one does not lose the identity
  localStorage.setItem(ID_KEY, id);
  writeCookie(ID_KEY, id);
  cachedId = id;
  return id;
};

// Automatic, human-readable device descriptor — no user input involved, so
// it cannot be typed wrongly. The stable short id ties it to the persistent
// device_id for forensics; OS/browser make history rows readable.
export const getDeviceLabel = (): string => {
  if (cachedLabel) return cachedLabel;
  const ua = navigator.userAgent;
  const os = /Mac/i.test(ua)
    ? 'macOS'
    : /Windows/i.test(ua)
      ? 'Windows'
      : /Linux/i.test(ua)
        ? 'Linux'
        : 'Unknown OS';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  const shortId = getDeviceId().replace(/-/g, '').slice(0, 6);
  cachedLabel = `${os} ${browser} #${shortId}`;
  return cachedLabel;
};
